import { useEffect } from "react";
import { Routes, Route } from "react-router-dom";
import "./app.css";
import { Home, NavMenu, Account, History } from "@components";
import { PwaInstallPrompt, OfflineScreen, useBrowserOnline } from "@controls";
import { Layout, Spin } from "antd";
import { Content, Footer } from "antd/es/layout/layout";
import { useAppDispatch, useCompany, useCompanyError, companyLoaded, companyLoadFailed } from "@redux";
import { isFrame, getIsVkSession, serverFetch, setStyleProperty, useGetIsPhone } from "@extensions";
import CookieConsent from "react-cookie-consent";
import Catalog from "components/catalog";
import Sched from "components/sched/sched";
import Sales from "components/sales/sales";
import PaymentSuccess from "components/payment/payment-success";
import PaymentError from "components/payment/payment-error";
import CompanyInactive from "components/company-inactive/company-inactive";
import RequireAuth from "components/require-auth/require-auth";
import AdSale from "components/ad-sale/ad-sale";
import { useDispatch, useSelector } from "react-redux";
import { userLoaded } from "@redux/actions";

const contrastColor = (color) => {
  if (!color || typeof color !== "string") return "#ffffff";

  const hex = color.trim().replace(/^#/, "");
  let r;
  let g;
  let b;

  if (/^[0-9a-fA-F]{3}$/.test(hex)) {
    r = Number.parseInt(hex[0] + hex[0], 16);
    g = Number.parseInt(hex[1] + hex[1], 16);
    b = Number.parseInt(hex[2] + hex[2], 16);
  } else if (/^[0-9a-fA-F]{6}$/.test(hex)) {
    r = Number.parseInt(hex.slice(0, 2), 16);
    g = Number.parseInt(hex.slice(2, 4), 16);
    b = Number.parseInt(hex.slice(4, 6), 16);
  } else {
    return "#ffffff";
  }

  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.62 ? "#111111" : "#ffffff";
};

// Загрузка компании по текущему хосту (см. docs/OnlineController.md)
const fetchCompany = () => async (dispatch) => {
  try {
    const companyData = await serverFetch("Online/Company");
    const company = {
      ...companyData,
      catalogBackgroundImage: companyData?.catalogBackgroundImage
        || (companyData?.backgroundImageId
          ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${companyData.backgroundImageId}.jpg`
          : ""),
    };
    console.log("Company loaded:", company);
    dispatch(companyLoaded(company));
    setStyleProperty('--default-color', company.color);
    setStyleProperty('--primary-color', company.color);
    setStyleProperty('--hover-color', company.color);
    setStyleProperty('--active-color', company.color);
    setStyleProperty('--hover-contrast-color', contrastColor(company.color));
    setStyleProperty(
      "--block-content-backround-img",
      company.catalogBackgroundImage ? `url("${company.catalogBackgroundImage}")` : "none"
    );
    document.title = company.name;
  } catch (error) {
    dispatch(companyLoadFailed(error?.userMessage));
  }
};

const App = () => {
  const dispatch = useAppDispatch();
  const company = useCompany();
  const companyError = useCompanyError();
  const isPhone = useGetIsPhone();
  const online = useBrowserOnline();
  const companyLoading = company == null && companyError == null;
  const companyInactive = company != null && !company.active;
  const catalogBackgroundStyle = company?.catalogBackgroundImage
    ? { backgroundImage: `url("${company.catalogBackgroundImage}")` }
    : undefined;
  // const companyInactive =
  //   companyError != null || (company != null && company.active === false);
  const d = useDispatch();
  const user = useSelector((s) => s.user);

  // При каждом запуске приложения перечитываем компанию
  useEffect(() => {
    dispatch(fetchCompany());
  }, [dispatch]);

  // const offerAutopay = company?.autopayConditionUrl;
  // const offerPayment = company?.paymentConditionUrl;

  // console.log(company)

  useEffect(() => {
    if (user != null) {
      serverFetch('auth/clientchecksession', { method: 'GET' })
        .then(() => { })
        .catch(() => { d(userLoaded()) });
    }
  }, [user, d]);

  // Keep favicon in sync with the current company.
  useEffect(() => {
    const faviconLink = document.querySelector("link[rel='icon']") || document.createElement("link");
    faviconLink.rel = "icon";
    faviconLink.href = company?.pwaImage
      || (company?.pictureFileName
        ? `${process.env.REACT_APP_API_BASE_URL}/api/image/${company.pwaImageId}.jpg`
        : "/images/logo.png");
    if (!document.querySelector("link[rel='icon']")) {
      document.head.appendChild(faviconLink);
    }
  }, [company?.pictureFileName, company?.pwaImage]);

  // Виджет чата на сайте
  useEffect(() => {
    if (isFrame || !company?.chatChannelCode) return;

    const scriptId = "sport24-chat-widget";
    if (document.getElementById(scriptId)) return;

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = `https://web2.sport24.cloud/widget.js?tokenCode=${encodeURIComponent(company.chatChannelCode)}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.getElementById(scriptId)?.remove();
      document.getElementById("misoft-chat-widget")?.remove();
    };
  }, [company?.chatChannelCode]);

  if (!online) {
    return <OfflineScreen />;
  }

  if (companyLoading) {
    return (
      <div className="app-loading">
        <Spin size="large" />
      </div>
    );
  }

  if (companyInactive) {
    return <CompanyInactive company={company} />;
  }

  return (
    <Layout className="app-layout" style={{ minHeight: "100vh" }}>
      {/* <div className="header-container">{isFrame || <Header />}</div> */}
      <div className="header-menu">
        <div className="header-container">
          <NavMenu />
        </div>
      </div>
      <Content className="app-content">
        <div className="content-container" style={catalogBackgroundStyle}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/catalog/:id" element={<Catalog />} />
            <Route path="/catalog" element={<Catalog />} />
            <Route path="/sched" element={<Sched />} />
            <Route
              path="/sales/*"
              element={
                <RequireAuth>
                  <Sales />
                </RequireAuth>
              }
            />
            <Route
              path="/history"
              element={
                <RequireAuth>
                  <History />
                </RequireAuth>
              }
            />
            <Route
              path="/account"
              element={
                <RequireAuth>
                  <Account />
                </RequireAuth>
              }
            />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-error" element={<PaymentError />} />
            <Route path="/*" element={<AdSale />} />
          </Routes>
          <div id="main-footer" style={{ height: 1 }} />
        </div>
      </Content>
      {!isFrame || getIsVkSession() ? (
        <>
          <Footer className="app-footer">
            <div className="app-footer-container">
              <div className="app-footer-brand">
                <a className="app-footer-brand-link" href="https://sport24.cloud/" target="_blank" rel="noreferrer">
                  (с) Виджет онлайн продаж
                </a>
              </div>

              <div className="app-footer-links" style={{ flexDirection: isPhone ? "column" : "row" }}>
                {company?.paymentConditionUrl && (
                  <a className="app-footer-link" href={company.paymentConditionUrl} target="_blank" rel="noreferrer">
                    Описание сервиса
                  </a>
                )}
                {company?.userAgreementUrl && (
                  <a className="app-footer-link" href={company.userAgreementUrl} target="_blank" rel="noreferrer">
                    Пользовательское соглашение
                  </a>
                )}
                {company?.rulesUrl && (
                  <a className="app-footer-link" href={company.rulesUrl} target="_blank" rel="noreferrer">
                    Условия и правила
                  </a>
                )}
                {company?.autopayConditionUrl && (
                  <a className="app-footer-link" href={company.autopayConditionUrl} target="_blank" rel="noreferrer">
                    Условия сервиса "Автоплатеж"
                  </a>
                )}
              </div>

              <div className="app-footer-actions">
                <PwaInstallPrompt />
              </div>
            </div>
          </Footer>
        </>
      ) : null}
      {isFrame || (
        <CookieConsent buttonText="Принять" onAccept={() => window.location.reload()}>
          Мы используем файлы cookie чтобы обеспечить Вам наилучшую работу на
          нашем веб-сайте. Продолжая использовать этот сайт, Вы даете согласие
          на использование файлов cookie
        </CookieConsent>
      )}
    </Layout>
  );
};

export default App;
